import { type Dispatch, type SetStateAction, useCallback } from 'react';

import { useMutation } from '@apollo/client/react';

import type { ReorderEvent } from '@modules/_shared/components/sortable-list/types';
import {
  STEP_LISTINGS_COMPARE_FRAGMENT,
  UPDATE_CMA_REPORTS_COMPARABLES,
} from '@modules/reports/cma-reports/cmaReportsQueries';
import ComparableListingsSettings from '@modules/reports/cma-reports/step-listing-compare/ComparableListingsSettings';
import ListingCardDraggable from '@modules/reports/cma-reports/step-listing-compare/ListingCardDraggable';
import type { CmaListingWithDistance } from '@modules/reports/cma-reports/step-listing-compare/types';

import { ListingsSelectionSection } from './ListingsSelectionSection';

type ComparableListingsSectionProps = {
  cmaReportId: string;
  cmaListingDistance: CmaListingWithDistance[];
  isRunningAutomaticAlgo: boolean;
  setListings: Dispatch<SetStateAction<CmaListingWithDistance[]>>;
  setManualSelectionOpen: () => void;
  setRefreshOpen: (open: boolean) => void;
  generateComparable: () => void;
  isPropertyComparableListingsSquareMeterCalculation: boolean;
  isShowFullIdxDescription: boolean;
  includeComparableListingDetails: boolean;
  showListingCards: boolean;
  comparableListingsMapZoomLevel: number | null;
};

const ComparableListingsSection = ({
  cmaReportId,
  cmaListingDistance,
  isRunningAutomaticAlgo,
  setListings,
  setManualSelectionOpen,
  setRefreshOpen,
  generateComparable,
  isPropertyComparableListingsSquareMeterCalculation,
  isShowFullIdxDescription,
  includeComparableListingDetails,
  showListingCards,
  comparableListingsMapZoomLevel,
}: ComparableListingsSectionProps) => {
  const [updateCmaReportsComparable] = useMutation(
    UPDATE_CMA_REPORTS_COMPARABLES,
  );

  const handleReorder = useCallback(
    ({ items, movedItem, toIndex }: ReorderEvent<CmaListingWithDistance>) => {
      const listingId = movedItem.listing?.id;
      if (!listingId) {
        return;
      }

      setListings(items);
      updateCmaReportsComparable({
        variables: {
          cma_report_id: cmaReportId,
          listing_id: listingId,
          order_nr: toIndex,
        },
        update: (cache, { data: newData }) => {
          const id = cache.identify({
            __typename: 'cma_reports',
            id: cmaReportId,
          });
          const updatedListings =
            newData?.update_cma_reports_comparables?.returning[0]?.cma_report
              ?.comparable_listings ?? [];

          const existingData = cache.readFragment({
            fragment: STEP_LISTINGS_COMPARE_FRAGMENT,
            fragmentName: 'StepListingsCompare',
            id,
          });

          const mergedData = {
            ...existingData,
            updated_at: newData?.update_cma_reports_by_pk?.updated_at,
            comparable_listings: updatedListings,
          };

          cache.writeFragment({
            fragment: STEP_LISTINGS_COMPARE_FRAGMENT,
            fragmentName: 'StepListingsCompare',
            id,
            data: mergedData,
          });
        },
      });
    },
    [cmaReportId, setListings, updateCmaReportsComparable],
  );

  return (
    <ListingsSelectionSection
      items={cmaListingDistance}
      loading={isRunningAutomaticAlgo}
      onAutomatic={() => {
        if (cmaListingDistance.length > 0) {
          setRefreshOpen(true);
        } else {
          generateComparable();
        }
      }}
      onManual={setManualSelectionOpen}
      onReorder={handleReorder}
      renderItem={({ item, index }) => (
        <ListingCardDraggable
          key={item.id}
          cmaReportId={cmaReportId}
          cmaListingDistance={item}
          index={index}
          setListings={setListings}
        />
      )}
    >
      <ComparableListingsSettings
        isPropertyComparableListingsSquareMeterCalculation={
          isPropertyComparableListingsSquareMeterCalculation
        }
        isShowFullIdxDescription={isShowFullIdxDescription}
        includeComparableListingDetails={includeComparableListingDetails}
        showListingCards={showListingCards}
        comparableListingsMapZoomLevel={comparableListingsMapZoomLevel}
      />
    </ListingsSelectionSection>
  );
};

export default ComparableListingsSection;
